// ADR 23.9.26v §4 — one online signal: React Query's own `onlineManager`, fed
// by the browser's events and by the transport's failures, so queries, the
// banner, the hints, the speech route and W3 all read the same value.
//
// ADR 29.9.26af §1 — an unreachable report is corrected by a probe, not by a
// blind 30 s wait. 23.9.26v assumed "the next real request is the probe", but
// under `networkMode: 'online'` an offline signal pauses every query, so no
// real request runs to disprove it: one transport blip read as a 30 s outage.

import { useSyncExternalStore } from 'react'
import { onlineManager } from '@tanstack/react-query'

import { probeLoopEnded, probeLoopStarted, type ProbeLoopEnding, type UnreachableReason } from './telemetry'

/** The backoff's cap: a lie-fi tab probes at most once per this long. */
export const RECHECK_MS = 30_000

/** The waits before each probe; the last step repeats. Ends with `RECHECK_MS` so the cap types as `number`. */
export const PROBE_BACKOFF_MS = [2_000, 4_000, 8_000, 16_000, RECHECK_MS] as const

/** One probe's own budget, after which it counts as a rejection. */
const PROBE_TIMEOUT_MS = 5_000

// `api/http.ts`'s one line, not `apiUrl`: `http.ts` imports this module
// through `networkFailure.ts`, so importing it back would close a cycle.
const HEALTH_URL = `${import.meta.env.VITE_API_BASE_URL ?? ''}/health`

/**
 * The episode a probe loop measures (ADR 29.9.26ag §1): when its first report
 * came, and how many `/health` fetches it has started, an in-flight one included.
 */
interface Episode {
  startedAt: number
  probes: number
}

/** The one probe loop: its pending timer, how far it has backed off, and its episode. */
let loop: ({ timer: ReturnType<typeof setTimeout>; step: number } & Episode) | null = null

/** Drop the loop silently — a test reset is not an ending a learner saw. */
function cancelLoop(): void {
  if (loop !== null) clearTimeout(loop.timer)
  loop = null
}

/** Drop the loop and say how its episode ended. */
function endLoop(endedBy: ProbeLoopEnding): void {
  const ended = loop
  if (ended === null) return
  cancelLoop()
  probeLoopEnded(endedBy, ended.probes, performance.now() - ended.startedAt)
}

// A fresh handle every step, so the episode's fields are carried forward.
function schedule(step: number, episode: Episode): void {
  const wait = PROBE_BACKOFF_MS[Math.min(step, PROBE_BACKOFF_MS.length - 1)]
  const { startedAt, probes } = episode
  loop = { timer: setTimeout(() => void tick(step), wait), step, startedAt, probes }
}

/**
 * One probe. Raw `fetch`, never `fetchOrUnreachable` (a failing probe must not
 * report itself), and header-less on purpose: a bare `HEAD` is a CORS simple
 * request, so it costs no preflight — the one sanctioned request without
 * `X-Client-Platform`, since it is not an API call.
 */
async function probe(): Promise<boolean> {
  try {
    await fetch(HEALTH_URL, { method: 'HEAD', cache: 'no-store', signal: AbortSignal.timeout(PROBE_TIMEOUT_MS) })
    return true // any readable response, whatever its status
  } catch {
    return false
  }
}

async function tick(step: number): Promise<void> {
  const self = loop
  if (self === null) return
  // The signal can turn online by a path the loop never saw (a setup re-run
  // after the last subscriber left); the browser saying offline is its own
  // recovery, through the `online` event. Both true reads as `other_online`.
  if (onlineManager.isOnline()) return endLoop('other_online')
  if (!navigator.onLine) return endLoop('browser_offline')
  self.probes += 1 // before the await, so a cancel mid-probe still counts it
  const reachable = await probe()
  // Cancelled (and perhaps replaced) while the probe was in flight; the canceller emitted.
  if (loop !== self) return
  if (reachable) {
    endLoop('probe')
    onlineManager.setOnline(navigator.onLine)
  } else {
    schedule(step + 1, self)
  }
}

/**
 * Replace React Query's default listener. It sets the browser's value
 * **immediately**: v5 otherwise starts online whatever the browser says, and an
 * offline launch would fire (and pause) nothing but failing requests.
 */
export function installOnlineSignal(): void {
  onlineManager.setEventListener((setOnline) => {
    setOnline(navigator.onLine)
    const online = () => {
      endLoop('browser_online') // the browser's word wins; silent with no loop running
      setOnline(true)
    }
    const offline = () => setOnline(false)
    window.addEventListener('online', online)
    window.addEventListener('offline', offline)
    return () => {
      window.removeEventListener('online', online)
      window.removeEventListener('offline', offline)
    }
  })
}

/**
 * The transport's report: go offline now, and probe `/health` after the first
 * backoff step. A report while a loop runs starts no second loop and keeps its
 * backoff, so a burst of failures from one blip cannot stack probes.
 */
export function reportUnreachable(reason: UnreachableReason = 'transport'): void {
  onlineManager.setOnline(false)
  if (loop !== null) return
  schedule(0, { startedAt: performance.now(), probes: 0 })
  probeLoopStarted(reason)
}

/**
 * A real response is the probe's answer, arriving by another road (ADR
 * 7.10.26ad §2): on a high-latency link the 5 s probe can time out while real
 * requests still land. Queries paused offline never fire, so this cannot loop.
 */
export function reportReachable(): void {
  if (onlineManager.isOnline()) return // the common case: one boolean read
  endLoop('response') // silent when no loop is running
  onlineManager.setOnline(navigator.onLine) // as the probe does: the browser's word still wins
}

/** Drop the loop, so a fake timer a test abandoned cannot strand the next report. */
export function resetOnlineSignalForTests(): void {
  cancelLoop()
}

const isOnline = () => onlineManager.isOnline()
// Module-level, so React never resubscribes on a re-render: the manager tears
// its listener down when its last subscriber leaves and re-runs the setup on
// the next — which re-reads `navigator.onLine` and would drop a lie-fi report.
const subscribe = (notify: () => void) => onlineManager.subscribe(notify)

/** The shared signal, as a render value. */
export function useOnlineStatus(): boolean {
  return useSyncExternalStore(subscribe, isOnline, isOnline)
}

const browserOnline = () => navigator.onLine
// Its own subscription (ADR 7.10.26ad §3): on lie-fi the shared signal is
// already offline, so a later browser `offline` event changes nothing there
// and `onlineManager` never notifies.
function subscribeBrowser(notify: () => void): () => void {
  window.addEventListener('online', notify)
  window.addEventListener('offline', notify)
  return () => {
    window.removeEventListener('online', notify)
    window.removeEventListener('offline', notify)
  }
}

/** What the browser itself says, as a render value. */
export function useBrowserOnline(): boolean {
  return useSyncExternalStore(subscribeBrowser, browserOnline, browserOnline)
}
